/**
 * Paliers de résolution d'un média — module pur, sans React.
 *
 * Le back génère les paliers strictement inférieurs à l'original ; un palier
 * plus grand retombe sur `max`. Il n'y a donc jamais de trou dans `variants`,
 * jamais d'image agrandie, et aucun poids par palier (l'API ne l'expose pas).
 *
 * `auto` n'est pas un palier : c'est la résolution intelligente du Web, où
 * `srcset` est posé sur la balise et le navigateur choisit selon l'écran et la
 * densité. Aucune sélection en JavaScript — le navigateur fait mieux, et ça
 * vaut aussi pour le premier rendu du site publié.
 */

export const AUTO_RESOLUTION = 'auto';

export const MEDIA_CONTEXTS = {
  web: 'web',
  pdf: 'pdf',
};

/** Défaut par contexte : le PDF veut du léger, le site veut du srcset. */
export const DEFAULT_RESOLUTION = {
  [MEDIA_CONTEXTS.web]: AUTO_RESOLUTION,
  [MEDIA_CONTEXTS.pdf]: 'pdf',
};

/** Ordre d'affichage — du plus lourd au plus léger. */
const VARIANT_ORDER = ['max', 'pdf', 'web_lg', 'web_md', 'web_sm'];

const VARIANT_LABELS = {
  max: 'Maximale',
  pdf: 'PDF',
  web_lg: 'Grande',
  web_md: 'Moyenne',
  web_sm: 'Petite',
};

const AUTO_LABEL = 'Automatique';

function variantLabel(key, variant) {
  const name = VARIANT_LABELS[key] || key;
  return variant?.width ? `${name} — ${variant.width} px` : name;
}

/** Le palier par défaut du contexte reste signalé quand on en change. */
function withRecommendation(label, value, context) {
  return value === DEFAULT_RESOLUTION[context] ? `${label} (recommandé)` : label;
}

/**
 * Options offertes à l'utilisateur : tous les paliers réellement présents dans
 * le payload — un palier `web_*` reste une image, rien n'interdit de la poser
 * dans un PDF pour l'alléger —, plus `auto` sur le Web. Deux paliers dont l'URL est la même (un
 * palier plus grand que l'original retombe sur `max`) ne sont montrés qu'une
 * fois : proposer deux fois la même image n'est pas un choix.
 */
export function resolutionOptions(media, context = MEDIA_CONTEXTS.web) {
  const variants = media?.variants || {};
  const options = [];
  const byUrl = new Map();

  if (context === MEDIA_CONTEXTS.web && media?.srcset) {
    options.push({
      value: AUTO_RESOLUTION,
      label: withRecommendation(AUTO_LABEL, AUTO_RESOLUTION, context),
    });
  }

  VARIANT_ORDER.forEach((key) => {
    const variant = variants[key];
    if (!variant?.url) return;

    const twin = byUrl.get(variant.url);
    if (twin) {
      /* Image plus petite que le palier : le back renvoie l'original pour ce
         palier. Une seule entrée, mais c'est le palier attendu par le contexte
         qui la porte — sans quoi une image de 900 px s'insérerait dans un PDF
         en « Maximale », et l'utilisateur chercherait en vain le choix « PDF ».
         Le libellé dit pourquoi elle n'est pas plus légère. */
      if (key === DEFAULT_RESOLUTION[context]) {
        twin.value = key;
        twin.label = `${variantLabel(key, variant)} (recommandé, taille d'origine)`;
      }
      return;
    }

    const option = {
      value: key,
      label: withRecommendation(variantLabel(key, variant), key, context),
    };
    byUrl.set(variant.url, option);
    options.push(option);
  });

  return options;
}

/** Palier retenu à l'insertion, ramené à ce que le média sait offrir. */
export function defaultResolutionFor(media, context = MEDIA_CONTEXTS.web) {
  const options = resolutionOptions(media, context);
  const wanted = DEFAULT_RESOLUTION[context];
  if (options.some((option) => option.value === wanted)) return wanted;
  return options[0]?.value || AUTO_RESOLUTION;
}

/**
 * URL à poser dans le bloc pour un palier donné.
 *
 * En `auto` c'est `srcset` qui décide, mais une balise a toujours besoin d'un
 * `src` : on y met le plus grand palier, celui que servira un navigateur qui
 * ignore `srcset`.
 */
export function resolveMediaUrl(media, resolution) {
  if (!media) return '';
  const variants = media.variants || {};
  if (resolution && resolution !== AUTO_RESOLUTION && variants[resolution]?.url) {
    return variants[resolution].url;
  }
  return variants.max?.url || media.source || '';
}

/** `srcset` n'a de sens qu'en `auto` : un palier choisi est un palier imposé. */
export function resolveMediaSrcset(media, resolution) {
  if (!media || resolution !== AUTO_RESOLUTION) return '';
  return media.srcset || '';
}

/**
 * Ce que l'on persiste avec le bloc / la variable : l'URL du palier, le
 * `srcset` quand il y en a un, l'id du média pour pouvoir recharger ses paliers
 * plus tard, et le palier lui-même. Rien n'est recalculé à l'affichage.
 */
export function mediaSelection(media, resolution) {
  return {
    url: resolveMediaUrl(media, resolution),
    srcset: resolveMediaSrcset(media, resolution),
    mediaId: media?.id ?? null,
    resolution: resolution || AUTO_RESOLUTION,
  };
}
